import { TYPE_EFFECTIVENESS } from './pokemonTypes';
import { WEATHER, TERRAIN } from '../data/battleConditions';

/**
 * Calculates the damage range for a move in a Pokémon battle.
 * @param {Object} attacker - The attacking Pokémon
 * @param {Object} defender - The defending Pokémon
 * @param {Object} move - The move being used
 * @param {Object} conditions - Battle conditions (weather, terrain)
 * @returns {Object} The minimum and maximum damage values and modifiers
 */
export const calculateDamageRange = (attacker, defender, move, conditions) => {
  const LEVEL = 50; // Standard competitive level
  
  // Calculate attacker's offensive stat
  const attackStat = move.category === 'Physical' ? attacker.stats.attack : attacker.stats.specialAttack;
  const defenseStat = move.category === 'Physical' ? defender.stats.defense : defender.stats.specialDefense;

  // Calculate base damage
  const baseDamage = Math.floor(
    Math.floor(
      Math.floor((2 * LEVEL / 5 + 2) * move.basePower * attackStat / defenseStat) / 50
    ) + 2
  );

  // Calculate modifiers
  let modifiers = 1.0;

  // STAB (Same Type Attack Bonus)
  if (attacker.teraType === move.type) {
    if (attacker.types.includes(move.type)) {
      modifiers *= 2.0; // 2x STAB for matching Tera and original type
    } else {
      modifiers *= 1.5; // 1.5x STAB for matching Tera type only
    }
  } else if (attacker.types.includes(move.type)) {
    modifiers *= 1.5; // 1.5x STAB for matching original type
  }

  // Type effectiveness
  let typeEffectiveness = 1.0;
  const defenderTypes = defender.teraType ? [defender.teraType] : defender.types;
  defenderTypes.forEach(defenderType => {
    if (TYPE_EFFECTIVENESS[move.type]?.[defenderType] !== undefined) {
      typeEffectiveness *= TYPE_EFFECTIVENESS[move.type][defenderType];
    }
  });
  modifiers *= typeEffectiveness;

  // Item effects
  if (attacker.item) {
    if ((attacker.item === 'Choice Band' && move.category === 'Physical') ||
        (attacker.item === 'Choice Specs' && move.category === 'Special')) {
      modifiers *= 1.5;
    } else if (attacker.item === 'Life Orb') {
      modifiers *= 1.3;
    } else if (attacker.item === 'Expert Belt' && typeEffectiveness > 1) {
      modifiers *= 1.2;
    }
  }

  // Weather effects
  if (conditions.weather) {
    if (WEATHER[conditions.weather].boost?.includes(move.type)) {
      modifiers *= 1.5;
    } else if (WEATHER[conditions.weather].reduce?.includes(move.type)) {
      modifiers *= 0.5;
    }
  }

  // Terrain effects
  if (conditions.terrain) {
    if (TERRAIN[conditions.terrain].boost?.includes(move.type)) {
      modifiers *= 1.3;
    } else if (TERRAIN[conditions.terrain].reduce?.includes(move.type)) {
      modifiers *= 0.5;
    }
  }

  // Random factor (0.85-1.00)
  const minDamage = Math.floor(baseDamage * modifiers * 0.85);
  const maxDamage = Math.floor(baseDamage * modifiers);

  return {
    min: minDamage,
    max: maxDamage,
    modifiers: {
      stab: attacker.teraType === move.type ? 
        (attacker.types.includes(move.type) ? 2.0 : 1.5) :
        (attacker.types.includes(move.type) ? 1.5 : 1.0),
      typeEffectiveness,
      itemEffect: attacker.item ? (
        (attacker.item === 'Choice Band' && move.category === 'Physical') ||
        (attacker.item === 'Choice Specs' && move.category === 'Special') ? 1.5 :
        attacker.item === 'Life Orb' ? 1.3 :
        attacker.item === 'Expert Belt' && typeEffectiveness > 1 ? 1.2 : 1.0
      ) : 1.0,
      weatherEffect: conditions.weather ? (
        WEATHER[conditions.weather].boost?.includes(move.type) ? 1.5 :
        WEATHER[conditions.weather].reduce?.includes(move.type) ? 0.5 : 1.0
      ) : 1.0,
      terrainEffect: conditions.terrain ? (
        TERRAIN[conditions.terrain].boost?.includes(move.type) ? 1.3 :
        TERRAIN[conditions.terrain].reduce?.includes(move.type) ? 0.5 : 1.0
      ) : 1.0,
      finalMultiplier: modifiers
    }
  };
};

/**
 * Calculates a Pokémon's stat value based on base stat, EVs, IVs, level, and nature.
 * @param {number} baseStat - The Pokémon's base stat value
 * @param {number} ev - Effort Value points in the stat
 * @param {number} iv - Individual Value points in the stat
 * @param {number} level - The Pokémon's level
 * @param {string} nature - The Pokémon's nature
 * @param {string} statName - The name of the stat being calculated
 * @returns {number} The calculated stat value
 */
export const calculateStat = (baseStat, ev, iv, level, nature, statName) => {
  // For HP stat
  if (statName === 'hp') {
    return Math.floor(((2 * baseStat + iv + Math.floor(ev/4)) * level) / 100) + level + 10;
  }
  
  // For other stats
  let stat = Math.floor(((2 * baseStat + iv + Math.floor(ev/4)) * level) / 100) + 5;
  
  // Apply nature modifier
  if (nature) {
    if ((nature === 'Adamant' && statName === 'attack') ||
        (nature === 'Modest' && statName === 'specialAttack') ||
        (nature === 'Impish' && statName === 'defense') ||
        (nature === 'Calm' && statName === 'specialDefense') ||
        (nature === 'Jolly' && statName === 'speed')) {
      stat = Math.floor(stat * 1.1);
    } else if ((nature === 'Modest' && statName === 'attack') ||
               (nature === 'Adamant' && statName === 'specialAttack') ||
               (nature === 'Calm' && statName === 'defense') ||
               (nature === 'Impish' && statName === 'specialDefense') ||
               (nature === 'Timid' && statName === 'attack')) {
      stat = Math.floor(stat * 0.9);
    }
  }
  
  return stat;
}; 